<!-- PortfolioValueChart.vue -->
<template>
  <div class="chart-container">
    <div class="chart-header">
      <h3>Portfolio Value Visualization</h3>
      <div class="chart-controls">
        <button 
          @click="toggleView" 
          class="btn-toggle"
          :class="{ active: showDetailed }"
        >
          {{ showDetailed ? 'Show Summary' : 'Show Details' }}
        </button>
      </div>
    </div>

    <div class="chart-wrapper">
      <HighChartsWrapper
        name="portfolio-value"
        :chart-type="chartType"
        :chart-options="highchartsOptions"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';

export default {
  name: 'PortfolioValueChart',
  components: {
    HighChartsWrapper
  },
  props: {
    yearRows: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    formatCurrency: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    const showDetailed = ref(false);
    const chartType = ref('column');

    // Colors for different categories
    const colors = {
      portfolio: '#9b59b6',      // Purple for total portfolio
      investments: '#3498db',    // Blue for investments
      cash: '#2ecc71',          // Green for cash
      withdrawals: '#f39c12',    // Orange for withdrawals
      contributions: '#27ae60',  // Dark green for contributions
      shortfall: '#e74c3c',      // Red for shortfall
      netSavings: '#34495e',     // Dark gray for net savings line
      // Palette for individual items
      investmentPalette: [
        '#2980b9', '#3498db', '#5dade2', '#85c1e2', '#aed6f1',
        '#d6eaf8', '#1e90ff', '#4169e1', '#0066cc', '#004c99'
      ],
      cashPalette: [
        '#27ae60', '#2ecc71', '#58d68d', '#82e0aa', '#abebc6',
        '#d5f4e6', '#22c55e', '#16a34a', '#15803d', '#166534'
      ]
    };

    // Filter liquid assets only
    const liquidAssets = computed(() => {
      return props.items
        .map((item, index) => ({ ...item, originalIndex: index }))
        .filter(item => 
          item.type === 'asset' &&
          (item.category === 'Investments' || item.category === 'Cash & Bank')
        );
    });

    // Separate by category
    const investmentAssets = computed(() => 
      liquidAssets.value.filter(item => item.category === 'Investments')
    );

    const cashAssets = computed(() => 
      liquidAssets.value.filter(item => item.category === 'Cash & Bank')
    );

    // Prepare chart data and options
    const highchartsOptions = computed(() => {
      const years = props.yearRows.map(r => r.year);

      return {
        chart: { height: 400, type: 'column' },
        title: { text: null },
        xAxis: { categories: years, title: { text: 'Year' } },
        yAxis: {
          title: { text: 'Portfolio Value ($)' },
          labels: {
            formatter: function() {
              return props.formatCurrency(this.value);
            }
          }
        },
        tooltip: {
          formatter() {
            return `<b>${this.x}</b><br/>Portfolio Value: <b>${props.formatCurrency(this.y)}</b>`;
          }
        },
        plotOptions: {
          column: { borderWidth: 0 }
        },
        series: [
          {
            name: 'Portfolio Value',
            data: props.yearRows.map(row => row.portfolioValue || 0),
            color: '#3498db' // similar blue to eMoney bar
          }
        ]
      };
    });


    const toggleView = () => {
      showDetailed.value = !showDetailed.value;
    };

    return {
      showDetailed,
      chartType,
      highchartsOptions,
      toggleView
    };
  }
};
</script>